<template>
  <div id="accordionSetupFeaturedImage" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingSetupFeaturedImage" class="accordion-header">
        <button
          aria-controls="collapseSetupFeaturedImage"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseSetupFeaturedImage"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="description" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.featuredImage") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseSetupFeaturedImage"
        aria-labelledby="headingSetupFeaturedImage"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionSetupFeaturedImage"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <div class="d-flex align-items-start gap-16">
            <div class="flex-grow-1">
              <v-uploader
                ref="uploader"
                :multiple="false"
                :max-items="1"
                :after-upload="onChange"
                :headers="{ 'X-case': clientTour }"
                route-prefix="api/s3"
                @removed="onChange"
              />
            </div>

            <div v-if="featuredImage && (featuredImage as any).id" class="flex-shrink-0">
              <v-picture 
                class="d-block image-preview-container shadow-sm cursor-pointer"
                title="Click to view full size"
                @click="openImagePreview"
              >
                <img 
                  :alt="$t('clientTour.featuredImage')" 
                  :src="(featuredImage as any).storageUrl || (featuredImage as any).storage_url || (featuredImage as any).originalUrl || (featuredImage as any).original_url"
                  @error="(e: any) => e.target.src = '/img/placeholder-image.png'" 
                />
              </v-picture>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-12">
            <v-button
              :disabled="disableButton || mediaUuids.length === 0"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="confirmMedia"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch, computed, getCurrentInstance } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import VUploader from "@/components/vendor/upload/VUploader.vue";
import Media from "@/api/objects/Media";
import Tour from "@/api/objects/Tour";
import { useUserStore } from "@/stores/user";
import VPicture from "@/components/vendor/basic/picture/VPicture.vue";
import { useToast } from "vue-toastification";
import { storeToRefs } from "pinia";

const props = defineProps<{
  agency: string;
  tour: Tour;
  clientTour: number;
}>();

const toast = useToast();
const userStore = useUserStore();
const { currentTour } = storeToRefs(userStore);

const media = ref<any[]>([]);
const disableButton = ref(false);
const featuredImage = ref<Media | null>(null);
const uploader = ref();

const mediaUuids = computed(() =>
  media.value.map((item) => ({
    uuid: item.attributes.uuid,
    name: item.attributes.name,
    collection: "featured_image",
  }))
);

watch(
  () => currentTour.value || props.tour,
  (newTour: any) => {
    const fImg = newTour?.featuredImage || newTour?.featured_image;
    if (fImg) {
      featuredImage.value = Array.isArray(fImg) ? fImg[0] : fImg;
    } else {
      featuredImage.value = null;
    }
  },
  { immediate: true, deep: true }
);

const onChange = () => {
  if (uploader.value) {
    media.value = uploader.value.mediaLibrary.state.media;
  }
};

const openImagePreview = () => {
  const url = (featuredImage.value as any)?.originalUrl || 
              (featuredImage.value as any)?.original_url || 
              (featuredImage.value as any)?.storageUrl || 
              (featuredImage.value as any)?.storage_url;
  if (url) {
    window.open(url, "_blank");
  }
};

const confirmMedia = async () => {
  try {
    disableButton.value = true;

    if (featuredImage.value && (featuredImage.value as any).id) {
      await api.media.delete("s3", (featuredImage.value as any).id);
    }

    const data = {
      media: mediaUuids.value,
      model_type: "case",
      collection: "featured_image",
      model_id: props.clientTour,
    };

    const response = await api.media.confirm("s3", data);
    toast.success("Featured image updated successfully");

    media.value = [];

    if (uploader.value) {
      uploader.value.mediaLibrary.state.media = [];
    }

    await reloadCase();
    
    // Explicitly update the local featuredImage after reload to ensure UI re-renders
    if (userStore.currentTour?.featuredImage) {
      featuredImage.value = Array.isArray(userStore.currentTour.featuredImage) 
        ? userStore.currentTour.featuredImage[0] 
        : userStore.currentTour.featuredImage;
    }
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || "Media confirmation failed.");
  } finally {
    disableButton.value = false;
  }
};

const reloadCase = async () => {
  await userStore.setCurrentCase(props.agency, Number(props.clientTour));
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

.image-preview-container {
  width: 100px;
  height: 60px;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid #ddd;
  
  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.featured-image-save-btn {
  border-radius: 6px !important;
  font-size: 12px !important;
  letter-spacing: 0.3px;
  padding: 0.45rem 0.9rem !important;
  background-color: #57C68A !important;
  border-color: #57C68A !important;
  color: #ffffff !important;
}

.featured-image-save-btn:hover {
  background-color: #45b478 !important;
  border-color: #45b478 !important;
}
</style>
